/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { SpecAndFeatureDataType } from '@apps/components/CarDetails/SpecAndFeature';
import SliderComponent from '@apps/components/Slider';
import Tooltip from '@apps/components/Tooltip';
import { priceFormat } from '@apps/utils/format-price';
import HelpIcon from '@mui/icons-material/Help';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useGetResponsive } from '@stack/helpers/lib/responsive';
import { TitleDivider } from '@stack/ui/components';
import Image, { StaticImageData } from 'next/image';

export type LoanCalculatorContainerProps = {
  data?: SpecAndFeatureDataType;
  carImage?: string | StaticImageData;
  rate?: number;
  depositRate?: number;
  loanTenure?: number;
  webView?: boolean;
  showTitle?: boolean;
  carTitle?: string;
  carBrand?: string;
};

/**
 * ===========================
 * MAIN
 * ===========================
 */

export const LoanCalculatorContainer: React.FC<LoanCalculatorContainerProps> = (
  props
) => {
  const {
    data,
    carImage,
    rate,
    depositRate,
    loanTenure,
    webView,
    showTitle = false,
    carTitle,
  } = props;
  const { isMobile } = useGetResponsive();

  //  =========== SLIDER MARKS

  const downPaymentMarks = [
    {
      value: 0,
      label: '0',
    },
    {
      value: 10,
      label: '10',
    },
    {
      value: 20,
      label: '20',
    },
    {
      value: 30,
      label: '30',
    },
    {
      value: 40,
      label: '40',
    },
    {
      value: 50,
      label: '50',
    },
    {
      value: 60,
      label: '60',
    },
    {
      value: 70,
      label: '70',
    },
    {
      value: 80,
      label: '80',
    },
    {
      value: 90,
      label: '90',
    },
    {
      value: 100,
      label: '100',
    },
  ];

  const loanPeriodMarks = [
    {
      value: 3,
      label: '3',
    },
    {
      value: 5,
      label: '5',
    },
    {
      value: 7,
      label: '7',
    },
    {
      value: 9,
      label: '9',
    },
  ];

  const carPriceText =
    'The selling price of the vehicle you intend to purchase.';

  const downPaymentText =
    'The interest rate on the loan that is being charged to you by the bank.';

  const price = Number(data?.price ?? 0);
  // =========== HOOKS
  const [value, setValue] = useState({
    depositRate: depositRate ?? 10,
    loanTenure: loanTenure ?? 7,
    price: price,
    rate: rate ?? 3.8,
  });

  useEffect(() => {
    setValue((prev) => ({
      ...prev,
      price: price,
    }));
  }, [data?.price]);

  // Change handler updates the state with the new slider value
  const onHandleChangeDownPayemnt = (event: Event, newValue: number) => {
    setValue((prev) => ({
      ...prev,
      depositRate: newValue,
    }));
  };

  const onHandleChangeLoanPeriod = (event: Event, newValue: number) => {
    setValue((prev) => ({
      ...prev,
      loanTenure: newValue,
    }));
  };

  // =========== CALCULATION
  const carPrice = value.price;
  const downPayment = value.depositRate;
  const loanPeriod = value.loanTenure;
  const interestRate = value.rate;

  const totalDownPayment = carPrice * (downPayment / 100);
  const totalLoanAmount = carPrice - totalDownPayment;

  const monthlyInstallment =
    (totalLoanAmount + totalLoanAmount * ((interestRate / 100) * loanPeriod)) /
    (loanPeriod * 12);

  return (
    <>
      <TitleDivider
        title={showTitle ? 'Loan Calculator' : undefined}
        webView={webView}
      >
        <Grid
          container
          sx={{ alignItems: 'stretch' }}
          spacing={6}
          direction={{
            xs: 'column',
            sm: 'row',
          }}
        >
          <Grid item xs={12} md={6} sx={{ height: '100%' }}>
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Stack
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  pb: 2,
                }}
                direction={{
                  xs: 'column',
                  md: 'row',
                }}
              >
                {/* Main Car Price */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    paddingY: 1,
                    pr: 0,
                    width: isMobile ? '100%' : '50%',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      fontWeight={500}
                      fontSize={'0.875rem'}
                      color={'#1E242F'}
                    >
                      {'Car Price (RM)'}
                    </Typography>
                    <Tooltip title={carPriceText}>
                      <HelpIcon
                        sx={{
                          color: '#737CBC',
                          height: 15,
                          width: 15,
                          marginLeft: '8px',
                        }}
                      />
                    </Tooltip>
                  </Box>
                  {data ? (
                    <Typography
                      fontWeight={700}
                      fontSize={'1.5rem'}
                      color="#31ACBF"
                    >
                      {priceFormat({ price: carPrice ?? 0, prefix: 'RM ' })}
                    </Typography>
                  ) : (
                    <TextField
                      id="outlined-basic"
                      variant="outlined"
                      type="number"
                      sx={{
                        backgroundColor: '#F7F7F7',
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: 'none',
                          },
                          '&:hover fieldset': {
                            border: 'none',
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none',
                          },
                        },
                      }}
                      value={carPrice}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setValue((prev) => ({
                          ...prev,
                          price: event.target.value as any,
                        }));
                      }}
                    />
                  )}
                </Box>
                {/* Interest */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    paddingY: 1,
                    pr: 0,
                    width: isMobile ? '100%' : '50%',
                  }}
                >
                  {/* Interest Rate Text and Help Icon */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      fontWeight={500}
                      fontSize={'0.875rem'}
                      color="#1E242F"
                    >
                      {'Interest Rate (%)'}
                    </Typography>
                    <Tooltip title={downPaymentText}>
                      <HelpIcon
                        sx={{
                          color: '#737CBC',
                          height: 15,
                          width: 15,
                          marginLeft: '8px',
                        }}
                      />
                    </Tooltip>
                  </Box>
                  <TextField
                    id="outlined-basic"
                    variant="outlined"
                    type="number"
                    sx={{
                      backgroundColor: '#F7F7F7',
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: 'none',
                        },
                        '&:hover fieldset': {
                          border: 'none',
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none',
                        },
                      },
                    }}
                    value={interestRate}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setValue((prev) => ({
                        ...prev,
                        rate: event.target.value as any,
                      }));
                    }}
                  />
                </Box>
              </Stack>

              <Box pt={{ md: 3 }}>
                <SliderComponent
                  sliderValue={priceFormat({
                    price: totalDownPayment,
                    prefix: 'RM ',
                  })}
                  selectedSliderValues={downPayment}
                  onSliderChange={onHandleChangeDownPayemnt as any}
                  title={'Down Payment (%)'}
                  marks={downPaymentMarks}
                />
              </Box>

              <Box>
                <SliderComponent
                  sliderValue={`${loanPeriod} Years`}
                  selectedSliderValues={loanPeriod}
                  onSliderChange={onHandleChangeLoanPeriod as any}
                  title={'Loan Period (Years)'}
                  marks={loanPeriodMarks}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6} sx={{ height: '100%' }}>
            <Box
              sx={{
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  padding: 4,
                  backgroundColor: '#F7F7F7',
                  alignContent: 'center',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      padding: 1,
                      width: '50%',
                    }}
                  >
                    <Typography
                      fontWeight={500}
                      fontSize={'0.875rem'}
                      color="#67708C"
                    >
                      {`Down Payment (${downPayment}%)`}
                    </Typography>
                    <Typography
                      fontWeight={700}
                      fontSize={{
                        xs: '1rem',
                        sm: '1.25rem',
                        md: '1.25rem',
                        lg: '1.25rem',
                      }}
                      color="#1E242F"
                    >
                      {priceFormat({ price: totalDownPayment, prefix: 'RM ' })}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      padding: 1,
                      width: '50%',
                    }}
                  >
                    <Typography
                      fontWeight={500}
                      fontSize={'0.875rem'}
                      color="#67708C"
                    >
                      Loan Amount
                    </Typography>
                    <Typography
                      fontWeight={700}
                      fontSize={{
                        xs: '1rem',
                        sm: '1.25rem',
                        md: '1.25rem',
                        lg: '1.25rem',
                      }}
                      color="#1E242F"
                    >
                      {priceFormat({ price: totalLoanAmount, prefix: 'RM ' })}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    marginRight: 2,
                    paddingTop: 4,
                    width: '100%',
                  }}
                >
                  <Typography
                    fontWeight={500}
                    fontSize={'0.875rem'}
                    color="#67708C"
                  >
                    {'Monthly Instalment (RM)'}
                  </Typography>
                  <Typography
                    fontWeight={700}
                    fontSize={{
                      xs: '1.25rem',
                      sm: '1.5rem',
                      md: '1.5rem',
                      lg: '2rem',
                    }}
                    color="#31ACBF"
                  >
                    {priceFormat({ price: monthlyInstallment, prefix: 'RM ' })}
                  </Typography>
                </Box>
              </Box>
              {carImage && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '80%',
                    right: '0%',
                    left: 0,
                    bottom: 0,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      transform: 'scaleX(-1)',
                      width: '50%',
                      aspectRatio: 4 / 3,
                    }}
                  >
                    <Image
                      src={carImage}
                      alt={`${carTitle} thumbnail image`}
                      loading={'lazy'}
                      width={0}
                      height={0}
                      sizes="100vw"
                      style={{
                        width: '100%',
                        height: '80%',
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Box>

            <Typography
              fontWeight={400}
              fontSize={'0.75rem'}
              pt={10}
              lineHeight={'24px'}
            >
              * This digital calculator is for demonstrating purposes only. The
              amounts, interest rates, and terms are varied according to both
              parties. The result in every case is approximate. It is not
              conclusive and is subject to terms and conditions from the
              respective financial institutions.
            </Typography>
          </Grid>
        </Grid>
      </TitleDivider>
    </>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default LoanCalculatorContainer;
